// eslint-disable-next-line max-classes-per-file
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import {
    ExecutePipelineActivity,
    ExecutePipelineActivityDuplicate,
    InvokePipelineConnectionType
} from 'app/model/pipeline/execute-pipeline.activity';
import { Pipeline } from '../../pipeline/pipeline';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';

import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { Owned } from 'app/model/base/named';
import { convertParameterValue, stringifyParameterValue } from 'app/shared/utils/parameter-converter.util';
import { FabricDataPipelinesLinkedService } from 'app/model/linkedservice/fabric-data-pipelines.linkedservice';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { AzureDataFactoryLinkedService } from 'app/model/linkedservice/azure-data-factory.linkedservice';
import { AzureSynapseAnalyticsWorkspaceLinkedService } from 'app/model/linkedservice/azure-synapse-analytics.linkedservice';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import {
    ActivityConnectionContext,
    IInvokePipelineActivityContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { findLinkedServiceFromContext, findPipelineFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { ModelService } from 'app/model/services/model.service';
import { IFrameService } from 'app/services/iframe.service';
import { CopilotNameMapperService } from 'app/services/copilot-name-mapper.service';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { getFabricLsForParameterizedConnections } from '../utils';
import { ModelConstants } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ExecutePipelineActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            pipelineId: '*',
            workspaceId: '*',
            pipelineName: '*',
            parameters: '*',
            waitOnCompletion: '*',
            operationType: '*'
        },
        externalReferences: '*'
    }
})
export class ExecutePipelineActivityAdapter extends ActivityAsyncAdapter<
    ExecutePipelineActivity,
    Model.ExecutePipelineActivity,
    IInvokePipelineActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(ExecutePipelineActivity);
    }

    public override async toFabricCopilotContext(
        activity: ExecutePipelineActivity
    ): Promise<IInvokePipelineActivityContext> {
        if (activity) {
            const activityContext: IInvokePipelineActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.WaitOnCompletion = activity.waitOnCompletion;
            activityContext.PipelineType = this._convertInvokePipelineTypeToContextType(activity.invokePipelineType);
            if (activity.alwaysShowNewWebExperience) {
                // InvokePipeline activity
                let lsAdapter;
                switch (activity.invokePipelineType) {
                    case InvokePipelineConnectionType.ADF:
                        lsAdapter = this.context.asyncAdapterFor<
                            AzureDataFactoryLinkedService,
                            Model.LinkedServiceResource,
                            ActivityConnectionContext
                        >(Metamodel.modelByName(AzureDataFactoryLinkedService.MODEL.name));
                        activityContext.Connection = await lsAdapter?.toFabricCopilotContext(
                            activity?.adfPipelinesLinkedService
                        );
                        activityContext.PipelineName = ValueAdapter.toResource(
                            activity,
                            ExecutePipelineActivity.PIPELINENAME,
                            true
                        );
                        break;
                    case InvokePipelineConnectionType.SYNAPSE:
                        lsAdapter = this.context.asyncAdapterFor<
                            AzureSynapseAnalyticsWorkspaceLinkedService,
                            Model.LinkedServiceResource,
                            ActivityConnectionContext
                        >(Metamodel.modelByName(AzureSynapseAnalyticsWorkspaceLinkedService.MODEL.name));
                        activityContext.Connection = await lsAdapter?.toFabricCopilotContext(
                            activity?.synapseWorkspacePipelinesLinkedService
                        );
                        activityContext.PipelineName = ValueAdapter.toResource(
                            activity,
                            ExecutePipelineActivity.PIPELINENAME,
                            true
                        );
                        break;
                    case InvokePipelineConnectionType.FABRIC:
                    default:
                        lsAdapter = this.context.asyncAdapterFor<
                            FabricDataPipelinesLinkedService,
                            Model.LinkedServiceResource,
                            ActivityConnectionContext
                        >(Metamodel.modelByName(FabricDataPipelinesLinkedService.MODEL.name));
                        activityContext.Connection = await lsAdapter?.toFabricCopilotContext(
                            activity?.fabricDataPipelinesLinkedService
                        );
                        activityContext.PipelineId = ValueAdapter.toResource(
                            activity,
                            ExecutePipelineActivity.PIPELINEID,
                            true
                        );
                        activityContext.WorkspaceId = ValueAdapter.toResource(
                            activity,
                            ExecutePipelineActivity.WORKSPACEID,
                            true
                        );
                        const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
                            this.context.contextData,
                            'CopilotNameMapperService'
                        );

                        const [invokePipelineName, invokePipelineWorkspaceName] = await Promise.all([
                            nameMapperService.getTridentArtifactNameById('pipeline', activityContext.PipelineId),
                            nameMapperService.getTridentArtifactNameById('workspace', activityContext.WorkspaceId)
                        ]);
                        
                        activityContext.PipelineName = invokePipelineName;
                        activityContext.WorkspaceName = invokePipelineWorkspaceName;
                        break;
                }
            } else {
                // ExecutePipeline activity
                if (activity.pipeline) {
                    activityContext.PipelineId = activity.pipeline.objectId;
                    activityContext.PipelineName = activity.pipeline.name;
                }
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IInvokePipelineActivityContext,
        model?: ExecutePipelineActivity
    ): Promise<ExecutePipelineActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            model.waitOnCompletion = context.WaitOnCompletion;
            if (context.PipelineType) {
                model.invokePipelineType = this._convertContextTypeToInvokePipelineType(context.PipelineType);
            }
            if (model.alwaysShowNewWebExperience) {
                // InvokePipeline activity
                if (context.Connection) {
                    const modelService = await getServiceInstance<ModelService>(
                        this.context.contextData,
                        'ModelService'
                    );
                    const iframeService = await getServiceInstance<IFrameService>(
                        this.context.contextData,
                        'IFrameService'
                    );
                    const linkedService = (await findLinkedServiceFromContext(context.Connection, { modelService, iframeService })).linkedService;
                    if (linkedService) {
                        let lsAdapter;
                        switch (context.PipelineType) {
                            case 'AzureDataFactory':
                                if (linkedService.getLinkedServiceType() === 'AzureDataFactory') {
                                    model.adfPipelinesLinkedService = linkedService as AzureDataFactoryLinkedService;
                                    lsAdapter = Metamodel.isModel(context.Connection.Type)
                                        ? this.context.asyncAdapterFor<
                                              AzureDataFactoryLinkedService,
                                              Model.LinkedServiceResource,
                                              ActivityConnectionContext
                                          >(Metamodel.modelByName(context.Connection.Type))
                                        : undefined;
                                    if (!lsAdapter) break;
                                    await lsAdapter.fromFabricCopilotContext(
                                        context.Connection,
                                        model.adfPipelinesLinkedService
                                    );
                                    ValueAdapter.toModel(
                                        model,
                                        ExecutePipelineActivity.PIPELINENAME,
                                        context.PipelineName
                                    );
                                }
                                break;
                            case 'Synapse':
                                if (linkedService.getLinkedServiceType() === 'SynapseWorkspace') {
                                    model.synapseWorkspacePipelinesLinkedService =
                                        linkedService as AzureSynapseAnalyticsWorkspaceLinkedService;
                                    lsAdapter = Metamodel.isModel(context.Connection.Type)
                                        ? this.context.asyncAdapterFor<
                                              AzureSynapseAnalyticsWorkspaceLinkedService,
                                              Model.LinkedServiceResource,
                                              ActivityConnectionContext
                                          >(Metamodel.modelByName(context.Connection.Type))
                                        : undefined;
                                    if (!lsAdapter) break;
                                    await lsAdapter.fromFabricCopilotContext(
                                        context.Connection,
                                        model.synapseWorkspacePipelinesLinkedService
                                    );
                                    ValueAdapter.toModel(
                                        model,
                                        ExecutePipelineActivity.PIPELINENAME,
                                        context.PipelineName
                                    );
                                }
                                break;
                            case 'Fabric':
                            default:
                                if (linkedService.getLinkedServiceType() === 'FabricDataPipelines') {
                                    model.fabricDataPipelinesLinkedService =
                                        linkedService as FabricDataPipelinesLinkedService;
                                    lsAdapter = Metamodel.isModel(context.Connection.Type)
                                        ? this.context.asyncAdapterFor<
                                              FabricDataPipelinesLinkedService,
                                              Model.LinkedServiceResource,
                                              ActivityConnectionContext
                                          >(Metamodel.modelByName(context.Connection.Type))
                                        : undefined;
                                    if (!lsAdapter) break;
                                    await lsAdapter.fromFabricCopilotContext(
                                        context.Connection,
                                        model.fabricDataPipelinesLinkedService
                                    );
                                    ValueAdapter.toModel(
                                        model,
                                        ExecutePipelineActivity.WORKSPACEID,
                                        context.WorkspaceId
                                    );
                                    ValueAdapter.toModel(model, ExecutePipelineActivity.PIPELINEID, context.PipelineId);
                                }
                                break;
                        }
                    }
                }
            } else {
                // ExecutePipeline activity
                if (context.PipelineId || context.PipelineName) {
                    const modelService = await getServiceInstance<ModelService>(
                        this.context.contextData,
                        'ModelService'
                    );
                    const pipeline = await findPipelineFromContext(context, { modelService });
                    if (pipeline) {
                        model.pipeline = pipeline;
                    }
                }
            }
        }
        return model;
    }

    public async postToModel(activity: ExecutePipelineActivity): Promise<void> {
        await super.postToModel(activity);
        if (!__IS_FABRIC_DPE__) {
            return;
        }
        if (activity.alwaysShowNewWebExperience) {
            const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
                this.context.contextData,
                'CopilotNameMapperService'
            );
            const pipelineId = ValueAdapter.toResource(activity, ExecutePipelineActivity.PIPELINEID, true);
            const workspaceId = ValueAdapter.toResource(activity, ExecutePipelineActivity.WORKSPACEID, true);

            if (pipelineId && !isLogicAppExpression(pipelineId)) {
                nameMapperService.getTridentArtifactNameById('pipeline', pipelineId);
            }

            if (workspaceId && !isLogicAppExpression(workspaceId)) {
                nameMapperService.getTridentArtifactNameById('workspace', workspaceId);
            }
        }
    }

    protected async _toResource(activity: ExecutePipelineActivity): Promise<Model.ExecutePipelineActivity> {
        const modelExecutePipelineActivity: Model.ExecutePipelineActivity = super._getCommonActivityProperties(
            activity,
            this._activityType()
        ) as Model.ExecutePipelineActivity;

        if (this.context.adapterConfig.convertToFabric || activity.alwaysShowNewWebExperience) {
            modelExecutePipelineActivity.typeProperties = {
                waitOnCompletion: activity.waitOnCompletion,
                operationType: this._getOperationType(activity.invokePipelineType)
            };

            if (modelExecutePipelineActivity.typeProperties.operationType === 'InvokeFabricPipeline') {
                let fabricDataPipelinesLinkedService = activity.fabricDataPipelinesLinkedService;
                if (this.context.adapterConfig.convertToFabric) {
                    fabricDataPipelinesLinkedService = new FabricDataPipelinesLinkedService('Execute Pipeline Linked Service');
                }
                modelExecutePipelineActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                    fabricDataPipelinesLinkedService,
                    activity.linkedServiceParameters
                );
                modelExecutePipelineActivity.typeProperties.pipelineId = ValueAdapter.toResource(
                    activity,
                    ExecutePipelineActivity.PIPELINEID
                );
                modelExecutePipelineActivity.typeProperties.workspaceId = ValueAdapter.toResource(
                    activity,
                    ExecutePipelineActivity.WORKSPACEID
                );
            } else if (modelExecutePipelineActivity.typeProperties.operationType === 'InvokeADFPipeline') {
                modelExecutePipelineActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                    activity.adfPipelinesLinkedService,
                    activity.linkedServiceParameters
                );
                modelExecutePipelineActivity.typeProperties.pipelineName = ValueAdapter.toResource(
                    activity,
                    ExecutePipelineActivity.PIPELINENAME
                );
            } else if (modelExecutePipelineActivity.typeProperties.operationType === 'InvokeSynapsePipeline') {
                modelExecutePipelineActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                    activity.synapseWorkspacePipelinesLinkedService,
                    activity.linkedServiceParameters
                );
                modelExecutePipelineActivity.typeProperties.pipelineName = ValueAdapter.toResource(
                    activity,
                    ExecutePipelineActivity.PIPELINENAME
                );
            }
        } else {
            modelExecutePipelineActivity.typeProperties = modelExecutePipelineActivity.typeProperties || {
                pipeline: undefined
            };

            modelExecutePipelineActivity.typeProperties.waitOnCompletion = activity.waitOnCompletion;
        }
        if (activity.parameters) {
            modelExecutePipelineActivity.typeProperties.parameters = {};
            activity.parameters.forEach((parameter: ParameterConfig) => {
                if (modelExecutePipelineActivity.typeProperties.parameters !== undefined) {
                    const parameterType = activity.pipeline
                        ? this._getPipelineParameterType(parameter.name, activity.pipeline)
                        : parameter.type;
                    const parameterValue = ValueAdapter.toResource(parameter, ParameterConfig.VALUE);
                    const isNullOrExpression =
                        parameterValue == null || ValueAdapter.isTemplated(parameter, ParameterConfig.VALUE);

                    try {
                        // Only do conversion from boolean types as changing other types conversion need more analysis on how pipelines will be affected
                        modelExecutePipelineActivity.typeProperties.parameters[parameter.name] =
                            isNullOrExpression ||
                            !parameterType ||
                            (parameterType !== 'bool' && parameterType !== 'string' && parameterType !== 'int')
                                ? parameterValue
                                : parameterType === 'string'
                                  ? stringifyParameterValue(parameter.value)
                                  : convertParameterValue(
                                        parameter.value,
                                        parameterType,
                                        'ExecutePipelineActivityAdapter',
                                        this.ensureLogger(),
                                        true /* don't parse as an object */
                                    );
                    } catch {
                        // any error just assign the raw value
                        modelExecutePipelineActivity.typeProperties.parameters[parameter.name] = parameterValue;
                    }
                }
            });
        }

        if (!activity.alwaysShowNewWebExperience && activity.pipeline) {
            modelExecutePipelineActivity.typeProperties.pipeline = ReferenceAdapter.toResource(
                activity.pipeline,
                this.context
            ) as Model.PipelineReference;
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(ExecutePipelineActivity, parsed);
        }
        return modelExecutePipelineActivity;
    }

    protected async _toModel(
        modelActivity: Model.ExecutePipelineActivity
    ): Promise<ExecutePipelineActivity | ExecutePipelineActivityDuplicate> {
        const activity: ExecutePipelineActivity | ExecutePipelineActivityDuplicate = this._createActivity(
            modelActivity.name
        );
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { pipeline: undefined };

        if (this._activityType() === 'InvokePipeline') {
            if (!modelActivity.linkedServiceName) {
                activity.isOldActivity = true;
            }

            if (!modelActivity.typeProperties.pipelineId && modelActivity.typeProperties.pipeline) {
                modelActivity.typeProperties.pipelineId = modelActivity.typeProperties.pipeline.referenceName;
            }

            ValueAdapter.toModel(activity, ExecutePipelineActivity.PIPELINEID, modelActivity.typeProperties.pipelineId);
            ValueAdapter.toModel(
                activity,
                ExecutePipelineActivity.PIPELINENAME,
                modelActivity.typeProperties.pipelineName
            );
            ValueAdapter.toModel(
                activity,
                ExecutePipelineActivity.WORKSPACEID,
                modelActivity.typeProperties.workspaceId
            );

            if (typeof modelActivity.typeProperties.pipelineId === 'string') {
                modelActivity.typeProperties.pipeline = {
                    referenceName: modelActivity.typeProperties.pipelineId,
                    type: 'PipelineReference'
                };
            }

            if (modelActivity.linkedServiceName !== undefined) {
                if (modelActivity.typeProperties.operationType === 'InvokeFabricPipeline') {
                    const ls =  getFabricLsForParameterizedConnections(
                        modelActivity.linkedServiceName,
                        ModelConstants.fabricDataPipelinesLinkedService, 
                        activity);
                     activity.fabricDataPipelinesLinkedService = ls ? (ls as FabricDataPipelinesLinkedService) :
                            ReferenceAdapter.toProxyModel<FabricDataPipelinesLinkedService>(
                                Metamodel.modelFor(FabricDataPipelinesLinkedService),
                                modelActivity.linkedServiceName,
                                this.context
                            );
                    activity.invokePipelineType = InvokePipelineConnectionType.FABRIC;
                } else if (modelActivity.typeProperties.operationType === 'InvokeADFPipeline') {
                    const ls =  getFabricLsForParameterizedConnections(
                        modelActivity.linkedServiceName,
                        ModelConstants.azureDatafactoryLinkedService, 
                        activity);
                     activity.adfPipelinesLinkedService = ls ? (ls as AzureDataFactoryLinkedService) :
                            ReferenceAdapter.toProxyModel<AzureDataFactoryLinkedService>(
                                Metamodel.modelFor(AzureDataFactoryLinkedService),
                                modelActivity.linkedServiceName,
                                this.context
                            );
                    activity.invokePipelineType = InvokePipelineConnectionType.ADF;
                } else if (modelActivity.typeProperties.operationType === 'InvokeSynapsePipeline') {
                    const ls =  getFabricLsForParameterizedConnections(
                        modelActivity.linkedServiceName,
                        ModelConstants.azureSynapseWorkspaceLinkedService, 
                        activity);

                     activity.synapseWorkspacePipelinesLinkedService = ls ? (ls as AzureSynapseAnalyticsWorkspaceLinkedService) :
                            ReferenceAdapter.toProxyModel<AzureSynapseAnalyticsWorkspaceLinkedService>(
                                Metamodel.modelFor(AzureSynapseAnalyticsWorkspaceLinkedService),
                                modelActivity.linkedServiceName,
                                this.context
                            );                        
                    activity.invokePipelineType = InvokePipelineConnectionType.SYNAPSE;
                }
            }
        }

        activity.waitOnCompletion = modelActivity.typeProperties.waitOnCompletion;

        if (modelActivity.typeProperties && modelActivity.typeProperties.parameters !== undefined) {
            activity.parameters = [];

            for (const propertyName in modelActivity.typeProperties.parameters) {
                const parameter = Owned.create<ParameterConfig>(ParameterConfig.MODEL, activity);
                parameter.name = propertyName;
                ValueAdapter.toModel(
                    parameter,
                    ParameterConfig.VALUE,
                    modelActivity.typeProperties.parameters[propertyName]
                );
                activity.parameters.push(parameter);
            }
        }
        if (modelActivity.typeProperties.pipeline) {
            activity.pipeline = ReferenceAdapter.toProxyModel<Pipeline>(
                Metamodel.modelFor(Pipeline),
                modelActivity.typeProperties.pipeline,
                this.context
            );
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelActivity, ExecutePipelineActivity.MODEL)
        );
        return activity;
    }

    protected _activityType(): string {
        return 'ExecutePipeline';
    }

    protected _createActivity(name: string): ExecutePipelineActivity {
        return ExecutePipelineActivity.MODEL.create<ExecutePipelineActivity>(name);
    }

    /**
     * Go to the source of truth (referenced pipeline) to find the correct type.
     */
    private _getPipelineParameterType(parameterName: string, pipeline: Pipeline): string {
        if (pipeline && pipeline.parameters) {
            const parameterFound = pipeline.parameters.find(
                (parameter) => parameter && parameter.name === parameterName && !!parameter._type
            );
            if (parameterFound && parameterFound._type) {
                return parameterFound._type.toLowerCase();
            }
        }
        return undefined;
    }

    private _getOperationType(connectionType: InvokePipelineConnectionType): string {
        switch (connectionType) {
            case InvokePipelineConnectionType.FABRIC:
                return 'InvokeFabricPipeline';
            case InvokePipelineConnectionType.ADF:
                return 'InvokeADFPipeline';
            case InvokePipelineConnectionType.SYNAPSE:
                return 'InvokeSynapsePipeline';
            default:
                return '';
        }
    }

    private _convertInvokePipelineTypeToContextType(
        connectionType: InvokePipelineConnectionType
    ): 'Fabric' | 'AzureDataFactory' | 'Synapse' {
        switch (connectionType) {
            case InvokePipelineConnectionType.FABRIC:
                return 'Fabric';
            case InvokePipelineConnectionType.ADF:
                return 'AzureDataFactory';
            case InvokePipelineConnectionType.SYNAPSE:
                return 'Synapse';
            default:
                return undefined;
        }
    }

    private _convertContextTypeToInvokePipelineType(
        contextType: 'Fabric' | 'AzureDataFactory' | 'Synapse'
    ): InvokePipelineConnectionType {
        switch (contextType) {
            case 'Fabric':
                return InvokePipelineConnectionType.FABRIC;
            case 'AzureDataFactory':
                return InvokePipelineConnectionType.ADF;
            case 'Synapse':
                return InvokePipelineConnectionType.SYNAPSE;
            default:
                return undefined;
        }
    }

    
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ExecutePipelineActivityDuplicate),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            pipelineId: '*',
            workspaceId: '*',
            pipelineName: '*',
            parameters: '*',
            waitOnCompletion: '*',
            operationType: '*'
        }
    }
})
export class ExecutePipelineActivityDuplicateAdapter extends ExecutePipelineActivityAdapter {
    public model(): IModel {
        return Metamodel.modelFor(ExecutePipelineActivityDuplicate);
    }

    protected _activityType(): string {
        return 'InvokePipeline';
    }

    protected _createActivity(name: string): ExecutePipelineActivityDuplicate {
        return ExecutePipelineActivityDuplicate.MODEL.create<ExecutePipelineActivityDuplicate>(name);
    }
}
